import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import {
  ARTIFACT_TYPES,
  ARTIFACT_TYPE_LABELS,
  type ArtifactType,
  type SubmissionView,
} from '@thottbot/shared';
import { apiSend, isRedirect, useArtifact } from '../api/client';
import { LoginGate } from '../components/LoginGate';
import { ArtifactForm, type ArtifactFormSubmit } from '../features/artifact/ArtifactForm';
import { NotFound } from './NotFound';
import { describeError } from './Submit';

function isType(t: string | undefined): t is ArtifactType {
  return !!t && (ARTIFACT_TYPES as readonly string[]).includes(t);
}

export function Edit() {
  const { type, slug } = useParams();
  const valid = isType(type) && !!slug;
  const result = useArtifact(valid ? type : 'quest', slug ?? '');
  const [done, setDone] = useState<SubmissionView | null>(null);
  const mutation = useMutation({
    mutationFn: (v: ArtifactFormSubmit & { artifactId: string; type: ArtifactType }) =>
      apiSend<SubmissionView>('POST', '/api/submissions', {
        kind: 'edit',
        type: v.type,
        artifactId: v.artifactId,
        name: v.name,
        data: v.data,
        note: v.note || undefined,
        verifiedInForever: v.verifiedInForever,
      }),
    onSuccess: (s) => setDone(s),
  });

  if (!valid) return <NotFound />;
  if (result.isPending) return <div className="page muted">Loading...</div>;
  if (result.isError || isRedirect(result.data))
    return <NotFound message="This entry cannot be edited." />;
  const { artifact } = result.data;
  const back = `/${artifact.type}/${artifact.slug}`;

  return (
    <div className="page">
      <title>{`Edit ${artifact.name}`}</title>
      <h1>
        Edit {ARTIFACT_TYPE_LABELS[artifact.type].singular}: <Link to={back}>{artifact.name}</Link>
      </h1>
      <LoginGate action="propose an edit">
        {done ? (
          <div className="notice" role="status">
            <b>Edit submitted for review.</b> The live entry stays unchanged until a moderator
            approves it. Follow it on <Link to="/me">your contributions page</Link> or go{' '}
            <Link to={back}>back to the entry</Link>.
          </div>
        ) : (
          <>
            <p className="muted">
              Changes go through the moderation queue. Tick "Verified in WoW: Forever" if you
              checked this in the Forever client.
            </p>
            <ArtifactForm
              type={artifact.type}
              mode="edit"
              initial={{ name: artifact.name, data: artifact.data }}
              busy={mutation.isPending}
              serverError={mutation.error ? describeError(mutation.error) : null}
              onSubmit={(v) =>
                mutation.mutate({ ...v, artifactId: artifact.id, type: artifact.type })
              }
            />
          </>
        )}
      </LoginGate>
    </div>
  );
}
